import {isEC3} from '../../../constants/evaluation-criteria.constants';
import {
  ECTab,
  ScoreMapEntry,
  SubcriteriaAttribute,
  SubcriteriaTab,
} from '../provider-questionnaire.types';
import {TemplateFillResult, TemplateFillSummary} from './template-fill.types';

const isAnswered = (attribute: SubcriteriaAttribute): boolean =>
  attribute.scoreAbsent || (attribute.score !== undefined && attribute.score !== null);

const collectSubcriteriaTabs = (ecTabs: ECTab[]): SubcriteriaTab[] =>
  ecTabs.flatMap((ecTab) =>
    isEC3(ecTab.ecNumber) && ecTab.moduleTabs
      ? ecTab.moduleTabs.flatMap((moduleTab) => moduleTab.subcriteriaTabs)
      : ecTab.subcriteriaTabs,
  );

const mapSubcriteriaTabs = (
  ecTabs: ECTab[],
  mapTab: (tab: SubcriteriaTab) => SubcriteriaTab,
): ECTab[] =>
  ecTabs.map((ecTab) =>
    isEC3(ecTab.ecNumber) && ecTab.moduleTabs
      ? {
          ...ecTab,
          moduleTabs: ecTab.moduleTabs.map((moduleTab) => ({
            ...moduleTab,
            subcriteriaTabs: moduleTab.subcriteriaTabs.map(mapTab),
          })),
        }
      : {...ecTab, subcriteriaTabs: ecTab.subcriteriaTabs.map(mapTab)},
  );

const coveredByTemplate = (
  attribute: SubcriteriaAttribute,
  scoreMap: Map<string, ScoreMapEntry>,
): boolean => scoreMap.has(attribute.directusId) && !attribute.hasChanges;

export function summarizeTemplateFill(
  ecTabs: ECTab[],
  scoreMap: Map<string, ScoreMapEntry>,
): TemplateFillSummary {
  const covered = collectSubcriteriaTabs(ecTabs)
    .flatMap((tab) => tab.attributes)
    .filter((attribute) => coveredByTemplate(attribute, scoreMap));
  const answeredCount = covered.filter(isAnswered).length;
  return {fillableCount: covered.length - answeredCount, answeredCount};
}

export function applyTemplateFill(
  ecTabs: ECTab[],
  scoreMap: Map<string, ScoreMapEntry>,
  overwriteAnswered: boolean,
): TemplateFillResult {
  const fillAttribute = (attribute: SubcriteriaAttribute): SubcriteriaAttribute => {
    const entry = scoreMap.get(attribute.directusId);
    if (!entry || attribute.hasChanges || (!overwriteAnswered && isAnswered(attribute))) {
      return attribute;
    }
    return {
      ...attribute,
      score: entry.score,
      scoreAbsent: entry.scoreAbsent,
      comment: entry.comment,
      capabilityLevel: entry.capabilityLevel,
      provisioningType: entry.provisioningType,
      singleStage: entry.singleStage,
    };
  };

  const filledTabs = mapSubcriteriaTabs(ecTabs, (tab) => ({
    ...tab,
    attributes: tab.attributes.map(fillAttribute),
  }));

  const originalAttributes = collectSubcriteriaTabs(ecTabs).flatMap((tab) => tab.attributes);
  const filledAttributes = collectSubcriteriaTabs(filledTabs)
    .flatMap((tab) => tab.attributes)
    .filter((attribute, index) => attribute !== originalAttributes[index]);

  return {ecTabs: filledTabs, filledAttributes};
}
